import { z } from "zod";
import { useParams } from "react-router-dom";
import Cookies from "js-cookie";
import { SubmitHandler, useForm } from "react-hook-form";
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AxiosError } from "axios";

import { Button } from "@/components/ui/button/button";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog/alert-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form/form";
import { Input } from "@/components/ui/input/input";

import { createNewLoanRepaymentRequest } from "@/config/mutation/employee";
import { Separator } from "@/components/ui/separator/separator";
import { UploadAttachment } from "../upload-attachement";

const formSchema = z.object({
  amount: z.coerce.number().min(0).default(0),
  attachment: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

export const CreateRepaymentRequest = () => {
  const empKey = Cookies.get("key") as string;
  const { loanId } = useParams() as {
    loanId: string;
  };
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      amount: 0,
      attachment: "",
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: createNewLoanRepaymentRequest(empKey),
  });

  const onSubmit: SubmitHandler<FormValues> = (values: FormValues) => {
    mutate(
      {
        ...values,
        loanId,
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: ["employee-loan", loanId],
          });

          await queryClient.invalidateQueries({
            queryKey: ["employee-loans"],
          });

          await queryClient.invalidateQueries({
            queryKey: ["employee-repayment-requests", loanId],
          });

          toast.success("Loan Repayment Request Created!");

          setTimeout(() => {
            form.setValue("amount", 0);
            form.setValue("attachment", "");
          }, 1000);

          setOpen(false);
        },

        onError: (error) => {
          console.log("ERROR WHILE CREATING NEW LOAN REQUEST", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  };

  return (
    <>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogTrigger>
          <Button size="sm">Create Repayment Request</Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="tracking-normal">
              Create Repayment Request
            </AlertDialogTitle>
          </AlertDialogHeader>

          <div className="">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <div>
                  <FormField
                    name="amount"
                    control={form.control}
                    render={({ field }) => {
                      return (
                        <FormItem>
                          <FormLabel>Amount</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              {...field}
                              disabled={isPending}
                            />
                          </FormControl>
                        </FormItem>
                      );
                    }}
                  />

                  <Separator className="my-4 mt-8" />

                  <FormField
                    control={form.control}
                    name="attachment"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Upload Attachment</FormLabel>
                        <FormControl>
                          <UploadAttachment
                            value={field.value}
                            onChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <AlertDialogFooter className="mt-5">
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <Button type="submit" disabled={isPending}>
                    Request
                  </Button>
                </AlertDialogFooter>
              </form>
            </Form>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
